import { defineStore, storeToRefs } from 'pinia'
import { type Raw, computed, ref, shallowRef } from 'vue'

import { AppNativeSDK } from '@/modules/native-app'

import { useAppStore } from './app-store'

type NativeAppMessage = {
    type: string
    payload: Record<string, unknown>
}
type NativeAppMessageHandler = (data: unknown) => void
type NativeAppOutgoingMessageHandler = (message: NativeAppMessage) => void

const ADJUST_ACTIVE_TIME_EVENT_TOKEN = 'tcw2fl'
const ACTIVE_TIME_TARGET_MS = 10 * 60 * 1000 // 10 minutes
const PING_INTERVAL_MS = 15_000 // 15 sec

export const useNativeAppStore = defineStore(
    'native_app',
    () => {
        const { platform, isNativeAppEnv } = storeToRefs(useAppStore())

        const sdk = shallowRef<Raw<AppNativeSDK>>()
        const elapsedActiveTimeMs = ref(0)
        const launchCount = ref(0)
        const isActiveTimeTracked = computed(() => elapsedActiveTimeMs.value >= ACTIVE_TIME_TARGET_MS)
        const messageHandlers = new Set<NativeAppMessageHandler>()
        const outgoingMessageHandlers = new Set<NativeAppOutgoingMessageHandler>()

        let pingIntervalId: ReturnType<typeof setInterval> | undefined

        const isAvailable = computed(() => sdk.value?.isAvailable ?? false)

        function init() {
            if (!isNativeAppEnv.value || sdk.value) {
                return
            }
            launchCount.value += 1
            sdk.value = new AppNativeSDK(platform.value)
            sdk.value.init()
            startActiveTracking()
        }

        function startActiveTracking() {
            if (isActiveTimeTracked.value || pingIntervalId !== undefined) {
                return
            }
            pingIntervalId = setInterval(() => {
                elapsedActiveTimeMs.value += PING_INTERVAL_MS
                if (isActiveTimeTracked.value) {
                    stopActiveTracking()
                    sendAdjustActiveTimeEvent()
                }
            }, PING_INTERVAL_MS)
        }

        function stopActiveTracking() {
            if (pingIntervalId !== undefined) {
                clearInterval(pingIntervalId)
                pingIntervalId = undefined
            }
        }

        function sendAdjustActiveTimeEvent() {
            postMessage('adjust.event', { eventToken: ADJUST_ACTIVE_TIME_EVENT_TOKEN })
        }

        function postMessage(type: string, payload: Record<string, unknown>) {
            const message = { type, payload }
            for (const handler of outgoingMessageHandlers) {
                handler(message)
            }

            return sdk.value?.postMessage(type, payload)
        }

        function addMessageHandler(handler: NativeAppMessageHandler) {
            messageHandlers.add(handler)
            const unsubscribeSdk = sdk.value?.addMessageHandler(handler)

            return () => {
                messageHandlers.delete(handler)
                unsubscribeSdk?.()
            }
        }

        function addOutgoingMessageHandler(handler: NativeAppOutgoingMessageHandler) {
            outgoingMessageHandlers.add(handler)

            return () => {
                outgoingMessageHandlers.delete(handler)
            }
        }

        function dispatchDebugMessage(data: unknown) {
            for (const handler of messageHandlers) {
                handler(data)
            }
        }

        function dispose() {
            stopActiveTracking()
            messageHandlers.clear()
            outgoingMessageHandlers.clear()
            sdk.value?.dispose()
        }

        return {
            sdk,
            isAvailable,
            elapsedActiveTimeMs,
            launchCount,
            init,
            postMessage,
            addMessageHandler,
            addOutgoingMessageHandler,
            dispatchDebugMessage,
            dispose,
        }
    },
    {
        persist: {
            key: 'native_app',
            pick: ['elapsedActiveTimeMs', 'launchCount'],
        },
    },
)
